/**
 * Resolves a pokemontcg.io set code (the lowercase id like "sv3pt5",
 * "swsh10", "me2pt5") into its friendly display name ("Scarlet & Violet—
 * 151", "Astral Radiance", "Mega Evolution—Phantasmal Flames"). Cached
 * across calls so a repeat lookup for the same code is free, and so a
 * deck-list import only hits the network once per unique set.
 *
 * Lookup order:
 *   1. Static index bundled at build time (apps/web/src/data/
 *      pokemonSetsIndex.json) — covers every set pokemontcg.io knew
 *      about when the app was last built. Zero network calls.
 *   2. In-session cache from a previous network lookup.
 *   3. Network: GET https://api.pokemontcg.io/v2/sets/{id} — fallback
 *      for sets released after the static index was generated.
 */

import POKEMON_SETS_INDEX from "@/data/pokemonSetsIndex.json";

interface IndexedSet {
  id: string;
  name: string;
  series?: string;
  releaseDate?: string;
  ptcgoCode?: string;
}

const STATIC_BY_ID = new Map<string, IndexedSet>();
const STATIC_BY_NAME = new Map<string, IndexedSet>();
const STATIC_BY_PTCGO = new Map<string, IndexedSet>();
for (const s of POKEMON_SETS_INDEX as IndexedSet[]) {
  if (s.id) STATIC_BY_ID.set(s.id.toLowerCase(), s);
  if (s.name) {
    // Name → most-recent set wins. The index is sorted by releaseDate so a
    // later set with the same name (rare but possible) overrides earlier.
    STATIC_BY_NAME.set(s.name.toLowerCase(), s);
  }
  if (s.ptcgoCode) {
    // ptcgoCode is the short 2-4 letter code used by eBay File Exchange,
    // TCGplayer, and most catalog sources (e.g. "PFL", "ASC"). Same
    // "most-recent wins" rule on duplicates.
    STATIC_BY_PTCGO.set(s.ptcgoCode.toLowerCase(), s);
  }
}

const SETS_API = "https://api.pokemontcg.io/v2/sets";

interface CachedEntry {
  /** null = looked up, set doesn't exist on pokemontcg.io. Don't retry. */
  name: string | null;
}

const cache = new Map<string, CachedEntry>();
const inflight = new Map<string, Promise<string | null>>();

export async function resolveSetName(setCode: string): Promise<string | null> {
  const code = setCode.trim();
  if (!code) return null;

  // 1. Static index — synchronous, no network.
  const key = code.toLowerCase();
  const byId = STATIC_BY_ID.get(key);
  if (byId) return byId.name;
  // Many catalog sources (eBay, TCGplayer) use the short ptcgoCode
  // instead of pokemontcg.io's full id — match it before falling back.
  const byPtcgo = STATIC_BY_PTCGO.get(key);
  if (byPtcgo) return byPtcgo.name;

  // 2. In-session cache from a prior network lookup.
  const cached = cache.get(code);
  if (cached) return cached.name;
  const existing = inflight.get(code);
  if (existing) return existing;

  // 3. Network fallback.
  const p = (async (): Promise<string | null> => {
    try {
      const res = await fetch(`${SETS_API}/${encodeURIComponent(code)}`, {
        headers: { Accept: "application/json" },
      });
      if (res.status === 404) {
        cache.set(code, { name: null });
        return null;
      }
      if (!res.ok) {
        // Transient error — don't cache, let next call retry.
        return null;
      }
      const json = (await res.json()) as { data?: { name?: string } };
      const name = json.data?.name?.trim() ?? null;
      cache.set(code, { name });
      return name;
    } catch {
      // Network error — don't cache.
      return null;
    } finally {
      inflight.delete(code);
    }
  })();
  inflight.set(code, p);
  return p;
}

/**
 * Heuristic: a card's setName is "unresolved" when it matches its setCode,
 * is purely lowercase alphanumeric, and is short (≤10 chars). Real set names
 * are mixed-case with spaces / punctuation. Used to spot pokemoncard.io
 * imports that haven't been backfilled yet.
 */
export function looksLikeUnresolvedSetName(
  setName: string,
  setCode: string,
): boolean {
  if (!setName || !setCode) return false;
  if (setName !== setCode) return false;
  return /^[a-z0-9]{1,10}$/.test(setName);
}

const SEARCH_API = "https://api.pokemontcg.io/v2/sets";

const codeCache = new Map<string, string | null>();
const codeInflight = new Map<string, Promise<string | null>>();

/**
 * Reverse of resolveSetName: given a friendly set name ("Stellar Crown",
 * "Shrouded Fable"), look up its pokemontcg.io set id and return it. Used
 * to backfill `setCode` on cards imported from sources that only provide
 * the name (eBay File Exchange, manual entry).
 *
 * Match preference: exact case-insensitive name match wins; otherwise we
 * take the most-recent release (the API already returns -releaseDate). On
 * ambiguity (e.g. multiple "Base Set" sets from different eras) the seller
 * can override the resolved code on the inventory detail page.
 */
export async function resolveSetCodeByName(
  setName: string,
): Promise<string | null> {
  const name = setName.trim();
  if (!name) return null;
  const key = name.toLowerCase();

  // 1. Static index — synchronous.
  const indexed = STATIC_BY_NAME.get(key);
  if (indexed?.id) return indexed.id;

  // 2. In-session cache.
  const cached = codeCache.get(key);
  if (cached !== undefined) return cached;
  const existing = codeInflight.get(key);
  if (existing) return existing;

  // 3. Network fallback.
  const p = (async (): Promise<string | null> => {
    try {
      // Use a name: query so the API does fuzzy matching for us; cap at 40
      // results which is the default page size.
      const escaped = name.replace(/"/g, '\\"');
      const url = `${SEARCH_API}?q=name:"${encodeURIComponent(escaped)}"&orderBy=-releaseDate&select=id,name&pageSize=40`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) return null;
      const json = (await res.json()) as {
        data?: { id?: string; name?: string }[];
      };
      const list = Array.isArray(json.data) ? json.data : [];
      if (list.length === 0) {
        codeCache.set(key, null);
        return null;
      }
      const exact = list.find(
        (s) => (s.name ?? "").toLowerCase() === key && s.id,
      );
      const winner = exact ?? list[0];
      const id = winner?.id ?? null;
      codeCache.set(key, id);
      return id;
    } catch {
      // Transient — don't cache.
      return null;
    } finally {
      codeInflight.delete(key);
    }
  })();
  codeInflight.set(key, p);
  return p;
}

/**
 * Heuristic for "needs setCode": setCode is blank but setName is filled.
 * Targets eBay File Exchange imports (which only carry the friendly name)
 * and manual cards where the seller didn't know the code.
 */
export function looksLikeMissingSetCode(
  setName: string,
  setCode: string,
): boolean {
  return setName.trim().length > 0 && setCode.trim().length === 0;
}

// ─── Distribution-label cards (Prize Pack, Deck Exclusives, etc.) ────────

/**
 * Set names that aren't real pokemontcg.io sets — they describe how the
 * card was distributed (preconstructed deck, prize pack, league promo,
 * etc.) and the actual base set has to be inferred from the card number.
 * Add to this list as we encounter more catalog-source labels.
 */
const DISTRIBUTION_LABEL_PATTERNS = [
  /^prize pack series\b/i,
  /^deck exclusive/i,
  /\bdeck exclusives?\b/i,
  /\bpromo cards?\b/i,
  /\bleague promo/i,
];

export function looksLikeDistributionLabel(setName: string): boolean {
  const trimmed = setName.trim();
  if (!trimmed) return false;
  return DISTRIBUTION_LABEL_PATTERNS.some((re) => re.test(trimmed));
}

export interface ResolvedCardSet {
  setId: string;
  setName: string;
}

/**
 * Looks up a card by name + number on pokemontcg.io's /v2/cards endpoint
 * and returns the most-recently-released matching set. Cached per
 * `(name, number)` tuple so duplicates in the import only hit the network
 * once.
 *
 * Used for cards whose stored `setName` is a distribution label rather
 * than a real set — the card number is the only reliable signal we have
 * to recover the base set.
 *
 * `cardNumber` may be in either "43" or "43/198" form; the numerator is
 * extracted and used in the query.
 */
const cardLookupCache = new Map<string, ResolvedCardSet | null>();
const cardLookupInflight = new Map<string, Promise<ResolvedCardSet | null>>();

const CARDS_API = "https://api.pokemontcg.io/v2/cards";

export async function resolveCardSetByNumber(
  cardName: string,
  cardNumber: string,
): Promise<ResolvedCardSet | null> {
  const name = cardName.trim();
  const numerator = cardNumber.trim().split("/")[0]?.trim() ?? "";
  if (!name || !numerator) return null;
  const key = `${name.toLowerCase()}|${numerator.toLowerCase()}`;

  const cached = cardLookupCache.get(key);
  if (cached !== undefined) return cached;
  const existing = cardLookupInflight.get(key);
  if (existing) return existing;

  const p = (async (): Promise<ResolvedCardSet | null> => {
    try {
      // Escape any double-quotes in the name for the Lucene query.
      const escaped = name.replace(/"/g, '\\"');
      const q = `name:"${escaped}" number:"${numerator}"`;
      const url =
        `${CARDS_API}?q=${encodeURIComponent(q)}` +
        `&select=id,name,number,set` +
        `&orderBy=-set.releaseDate&pageSize=10`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) return null;
      const json = (await res.json()) as {
        data?: { set?: { id?: string; name?: string } }[];
      };
      const list = Array.isArray(json.data) ? json.data : [];
      const hit = list.find((c) => c.set?.id && c.set?.name);
      if (!hit?.set?.id || !hit?.set?.name) {
        cardLookupCache.set(key, null);
        return null;
      }
      const out: ResolvedCardSet = {
        setId: hit.set.id,
        setName: hit.set.name,
      };
      cardLookupCache.set(key, out);
      return out;
    } catch {
      // Transient — don't cache.
      return null;
    } finally {
      cardLookupInflight.delete(key);
    }
  })();
  cardLookupInflight.set(key, p);
  return p;
}
